import type { Invoice, InvoiceCheck, InvoiceItem } from "@/lib/api/types";
import { liveOpenTotals } from "./groupPlates";
import { roundMoney } from "./pricing";
import { partyShares } from "./partyShares";
import { paidPlateIds } from "./settleSheet";
import { proRatedBillDiscount } from "./billDiscount";

/**
 * THE PRE-BILL CHECK (round 5) — the rules, pure, so the till, the floor tile
 * and the printed paper read one copy of each.
 *
 * A check is the bill as it stands, printed for the guests while the table
 * stays OPEN. The server records it (`printCheck`) with the total IT priced;
 * everything here only compares that record with the bill as it is now, or
 * lays out the paper. Nothing in this file decides money: the totals are
 * `liveOpenTotals`, the same function the order panel's foot renders.
 */

/** The splits the check offers. One guest is no split; five is the design's. */
export const CHECK_SPLIT_CHOICES = [2, 3, 4, 5] as const;
export type CheckSplitGuests = (typeof CHECK_SPLIT_CHOICES)[number];

/**
 * Half a piastre. The server prices the check through the close's own
 * rounding and the screens re-derive the live total through theirs; the two
 * agree to the piastre, so anything under half of one is float noise and
 * anything at or above it is a bill that moved.
 */
const SAME_TOTAL_EPSILON = 0.005;

export type CheckFreshness = "fresh" | "stale";

/**
 * Is the check in the guests' hands still the bill?
 *
 * `null` when there is no check, or when the bill has nothing on it — a check
 * of an empty bill is not a state worth a word on a tile or under a footer.
 */
export function checkFreshness(
  check: Pick<InvoiceCheck, "total"> | null | undefined,
  liveTotal: number,
): CheckFreshness | null {
  if (!check || liveTotal <= 0) return null;
  return Math.abs(check.total - liveTotal) < SAME_TOTAL_EPSILON
    ? "fresh"
    : "stale";
}

/**
 * An even split, to the piastre: the shares always add up to the amount.
 *
 * 100.00 between three is 33.34 + 33.33 + 33.33, never three 33.33s that
 * leave a piastre on the table. The odd piastres go to the first shares, so
 * the list reads largest first. Printed arithmetic, not a settlement — the
 * tenders still go through the till (B43 decided against equal-share paying).
 */
export function splitShares(amount: number, guests: number): number[] {
  const count = Math.max(1, Math.floor(guests));
  const piastres = Math.max(0, Math.round(roundMoney(amount) * 100));
  const base = Math.floor(piastres / count);
  const remainder = piastres - base * count;
  return Array.from(
    { length: count },
    (_, index) => (base + (index < remainder ? 1 : 0)) / 100,
  );
}

/**
 * The check's clock — `20:15`, 24-hour, Western digits in both languages, in
 * the STORE's zone rather than the tablet's: the paper is a document about the
 * restaurant, the same reason `formatRefundStamp` zones the refund slip.
 */
export function formatCheckClock(
  instant: string,
  timezone: string | null | undefined,
  locale: "ar" | "en",
): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return "";
  const tag = locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  };
  if (timezone) {
    try {
      return new Intl.DateTimeFormat(tag, { ...options, timeZone: timezone }).format(at);
    } catch {
      // An unknown zone string throws; fall back to the device's zone rather
      // than printing no time at all.
    }
  }
  return new Intl.DateTimeFormat(tag, options).format(at);
}

/** The plates a check lists: on the bill, and not struck off it. */
export function checkPlates(items: readonly InvoiceItem[]): InvoiceItem[] {
  return items.filter(
    (item) =>
      item.fulfillmentState !== "PENDING" &&
      item.fulfillmentState !== "DRAFT" &&
      item.fulfillmentState !== "VOIDED",
  );
}

export interface CheckMoney {
  subtotal: number;
  discountTotal: number;
  /**
   * D146 — the discount at the bill as the paper subtracts it from the
   * subtotal (its own «خصم» line); 0 when none.
   */
  billDiscount: number;
  serviceChargeTotal: number;
  vatTotal: number;
  deliveryTotal: number;
  /** The bill's live total — what the server stamped as the check's total. */
  total: number;
  /** Money already taken against this bill (a reopened, part-paid bill). */
  paid: number;
  /** What the guests are asked for: the total less what is already in. */
  toPay: number;
}

/**
 * The figures a check prints, from the bill as it is now.
 *
 * `total` is `liveOpenTotals` — the same live total the order panel's foot
 * shows and the server stamps — so the paper, the screen and the record are
 * one number. `paid` is the ledger's own `tenderedTotal`: on an ordinary open
 * bill it is 0 and the "already paid" line does not print.
 */
export function checkMoney(invoice: Invoice): CheckMoney {
  const totals = liveOpenTotals(invoice);
  const paid = roundMoney(invoice.tenderedTotal ?? 0);
  return {
    subtotal: totals.subtotal,
    discountTotal: totals.discountTotal,
    billDiscount: totals.billDiscountGross,
    serviceChargeTotal: totals.serviceChargeTotal,
    vatTotal: totals.vatTotal,
    deliveryTotal: roundMoney(
      (invoice.deliveryFee ?? 0) + (invoice.deliveryFeeVat ?? 0),
    ),
    total: totals.total,
    paid,
    toPay: Math.max(0, roundMoney(totals.total - paid)),
  };
}

/**
 * The till's note under the footer, decided: whether there is one, and which.
 * An OPEN bill with something on it and a check on record — nothing else
 * carries a note.
 */
export function tillCheckNote(
  invoice: Invoice,
): { freshness: CheckFreshness; at: string } | null {
  if (invoice.status !== "OPEN" || !invoice.lastCheck) return null;
  const freshness = checkFreshness(
    invoice.lastCheck,
    liveOpenTotals(invoice).total,
  );
  return freshness ? { freshness, at: invoice.lastCheck.at } : null;
}

/**
 * THE BILL FOR SOME OF ITS PLATES (founder, 2026-10-07): the same bill holding
 * only the ticked plates, for the partial bill's paper and the selection's
 * total. Service and VAT then price on those plates alone; the delivery fee
 * and any money already taken belong to the whole bill, so they are left off.
 */
export function invoiceForPlates(invoice: Invoice, plateIds: Iterable<string>): Invoice {
  const keep = new Set(plateIds);
  return {
    ...invoice,
    items: invoice.items.filter((item) => keep.has(item.id)),
    deliveryFee: 0,
    deliveryFeeVat: 0,
    tenderedTotal: 0,
    // D146: the whole bill's discount factor, never its fixed amount, so a
    // partial bill takes its own share of the discount (the one pro-rating rule).
    billDiscount: proRatedBillDiscount(invoice),
  };
}

/**
 * THE BILL WITHOUT WHAT IS ALREADY PAID (founder, 2026-10-07): a guest who
 * paid for their plates by item does not see them on the next bill. Plates of
 * a party that is square leave the list, and that party's money leaves "paid
 * so far" with them; money taken as a typed amount names no plate, so it stays
 * on the bill as "paid so far".
 */
export function invoiceForCheck(invoice: Invoice): Invoice {
  const paid = paidPlateIds(invoice);
  if (paid.size === 0) return invoice;
  const squareMoney = partyShares(invoice)
    .filter((share) => share.isSettled)
    .reduce((sum, share) => sum + share.paid, 0);
  return {
    ...invoice,
    items: invoice.items.filter((item) => !paid.has(item.id)),
    tenderedTotal: Math.max(0, roundMoney((invoice.tenderedTotal ?? 0) - squareMoney)),
    // D146: the remaining plates keep their share of the discount, by factor.
    billDiscount: proRatedBillDiscount(invoice),
  };
}
